import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PiArrowLeftDuotone,
  PiTrashDuotone,
  PiCalendarDuotone,
  PiListBulletsDuotone,
  PiWarningDuotone,
  PiTrophyDuotone,
} from 'react-icons/pi';
import {
  copyExternalEvent,
  createEvent,
  getEvent,
  searchExternalEvents,
  updateEvent,
} from '../api/eventService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Input from '../shared/common/Input';
import Select from '../shared/common/Select';
import DateTimePicker from '../shared/common/DateTimePicker';
import EmptyState from '../shared/common/EmptyState';
import { SkeletonCard } from '../shared/common/Skeleton';
import {
  EVENT_CATEGORY_DEFAULT,
  EVENT_CATEGORY_OPTIONS,
  EVENT_CATEGORY_OTHER,
} from '../models/eventCategories';
import { toDateTimeLocalValue, toUserOffsetDateTime } from '../shared/utils/dateTime';
import { readExternalEventCopySearch } from '../shared/utils/externalEventCopyFlow';
import {
  formatAllowedOutcomes,
  generateOutcomesFromDrawPolicy,
  getApplicableScoringRules,
  getDefaultDrawPolicy,
  getDrawPolicyOptions,
  isScoreBasedCategory,
} from '../shared/business/scoring/eventCategoryCapabilities';
import { outcomeCodeLabel } from '../shared/business/scoring/mainOutcomeHelpers';
import { OutcomeScheme } from '../models/eventType';
import { listAllEventTypes } from '../api/eventTypeService';
import { outcomeSchemeLabel, outcomeSchemeIcon, outcomeSchemeDescription, isCustomScheme } from '../shared/business/eventType/outcomeSchemeHelpers';
import { generateOutcomes as generateOutcomesFromEventType, formatAllowedOutcomes as formatEventTypeOutcomes } from '../shared/business/eventType/eventTypeCapabilities';

const FORBIDDEN_STATUS = 403;
const BAD_REQUEST_STATUS = 400;
const EXTERNAL_EVENT_STATE_KEY = 'externalEvent';
const COPY_SOURCE_FALLBACK = 'выбранный матч внешнего API';
const COPY_PREFILL_RETRY_MESSAGE = 'Не удалось предзаполнить событие. Вернись в поиск внешних матчей и выбери копирование ещё раз.';
const COPY_PREFILL_NOT_FOUND_MESSAGE = 'Выбранный внешний матч не найден в ответе API. Попробуй снова из поиска внешних событий.';
const EXTERNAL_EVENTS_PATH = '/events/external';
const EVENTS_PATH = '/events';

const getEventStartDate = (event) => event?.startDate || event?.eventDate || event?.date;

const isSameExternalEvent = (event, externalEventId) => (
  Boolean(event?.externalId) && String(event.externalId) === String(externalEventId)
);

const sanitizeOutcomes = (items) => (Array.isArray(items) ? items.filter(Boolean) : []);

const formatCopySourceTitle = (event, externalEventId) => event?.title || event?.externalId || externalEventId || COPY_SOURCE_FALLBACK;

const getCopyPrefillError = (error) => (
  error?.response?.data?.message || COPY_PREFILL_RETRY_MESSAGE
);

/** Маппинг slug встроенного EventType → EventCategory. */
const BUILT_IN_SLUG_TO_CATEGORY = {
  football: 'FOOTBALL',
  hockey: 'HOCKEY',
  basketball: 'BASKETBALL',
  tennis: 'TENNIS',
  volleyball: 'VOLLEYBALL',
  cybersport: 'CYBERSPORT',
  other: 'OTHER',
};

const categoryFromEventType = (et) => BUILT_IN_SLUG_TO_CATEGORY[et?.slug] || EVENT_CATEGORY_OTHER;

export default function CreateEventPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const copyPrefill = useMemo(() => readExternalEventCopySearch(location.search), [location.search]);
  const copyExternalEventId = !isEditMode ? copyPrefill.externalEventId : '';
  const copyCompetition = copyPrefill.competition;
  const isCopyMode = Boolean(copyExternalEventId);
  const pageTitle = isEditMode
    ? 'Редактировать событие'
    : isCopyMode
      ? 'Скопировать как своё событие'
      : 'Создать событие';
  const backPath = isEditMode ? `/events/${id}` : isCopyMode ? EXTERNAL_EVENTS_PATH : EVENTS_PATH;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState(EVENT_CATEGORY_DEFAULT);
  const [customCategory, setCustomCategory] = useState('');
  const [drawPolicy, setDrawPolicy] = useState(() => getDefaultDrawPolicy(EVENT_CATEGORY_DEFAULT));
  const [outcomes, setOutcomes] = useState([]);
  const [newOutcome, setNewOutcome] = useState('');
  const [copySourceTitle, setCopySourceTitle] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [eventType, setEventType] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [notEditable, setNotEditable] = useState(false);

  /** Если выбран EventType, форма полностью определяется типом. */
  const isEventTypeDriven = Boolean(eventType);
  const scoreBased = isScoreBasedCategory(category);
  const outcomeScheme = eventType?.outcomeScheme || null;
  const isSchemeBased = Boolean(outcomeScheme && outcomeScheme !== OutcomeScheme.SCORE_BASED);
  const effectiveScoreBased = scoreBased || outcomeScheme === OutcomeScheme.SCORE_BASED;

  const applyExternalEventPrefill = useCallback((event) => {
    const start = getEventStartDate(event);
    const end = event?.endDate || start;
    const eventCategory = event?.category || EVENT_CATEGORY_DEFAULT;
    setTitle(event?.title || '');
    setDescription(event?.description || '');
    setStartDate(toDateTimeLocalValue(start));
    setEndDate(toDateTimeLocalValue(end));
    setCategory(eventCategory);
    setCustomCategory(event?.customCategory || '');
    setDrawPolicy(event?.drawPolicy || getDefaultDrawPolicy(eventCategory));
    setOutcomes(sanitizeOutcomes(event?.outcomes));
    setCopySourceTitle(formatCopySourceTitle(event, copyExternalEventId));
    setErrors((prev) => ({ ...prev, global: undefined }));
  }, [copyExternalEventId]);

  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        const types = await listAllEventTypes();
        setEventTypes(Array.isArray(types) ? types : []);
      } catch {
        // Некритично
      }
    };
    loadEventTypes();
  }, []);

  useEffect(() => {
    if (!eventTypeId) {
      setEventType(null);
      return;
    }
    const found = eventTypes.find((et) => String(et.id) === String(eventTypeId));
    setEventType(found || null);
  }, [eventTypeId, eventTypes]);

  // При выборе EventType автоматически подстраиваем категорию, drawPolicy и исходы
  useEffect(() => {
    if (!eventType) return;
    const resolvedCategory = categoryFromEventType(eventType);
    setCategory(resolvedCategory);
    setCustomCategory(resolvedCategory === EVENT_CATEGORY_OTHER ? eventType.name : '');
    if (eventType.outcomeScheme === OutcomeScheme.SCORE_BASED && eventType.scoreDrawPolicy) {
      setDrawPolicy(eventType.scoreDrawPolicy);
    } else if (isScoreBasedCategory(resolvedCategory)) {
      setDrawPolicy(getDefaultDrawPolicy(resolvedCategory));
    }
    if (eventType.outcomeScheme === OutcomeScheme.CUSTOM) {
      setOutcomes([]);
    } else {
      const generated = generateOutcomesFromEventType(eventType);
      if (generated.length > 0) setOutcomes(generated);
    }
    setErrors((prev) => ({ ...prev, customCategory: undefined, outcomes: undefined, drawPolicy: undefined }));
  }, [eventType]);

  useEffect(() => {
    document.title = `BetMate — ${pageTitle}`;
  }, [pageTitle]);

  useEffect(() => {
    if (!isEditMode) return;

    setInitialLoading(true);
    getEvent(id)
      .then((event) => {
        if (!event.editable) {
          setNotEditable(true);
          return;
        }
        const eventCategory = event.category || EVENT_CATEGORY_OTHER;
        setTitle(event.title || '');
        setDescription(event.description || '');
        setStartDate(toDateTimeLocalValue(getEventStartDate(event)));
        setEndDate(toDateTimeLocalValue(event.endDate || getEventStartDate(event)));
        setCategory(eventCategory);
        setCustomCategory(event.customCategory || '');
        setDrawPolicy(event.drawPolicy || getDefaultDrawPolicy(eventCategory));
        setOutcomes(Array.isArray(event.outcomes) ? event.outcomes : []);
        if (event.eventTypeId) {
          setEventTypeId(String(event.eventTypeId));
        }
      })
      .catch(() => setErrors({ global: 'Не удалось загрузить событие для редактирования.' }))
      .finally(() => setInitialLoading(false));
  }, [id, isEditMode]);

  useEffect(() => {
    if (isEditMode || !copyExternalEventId) return;

    const stateEvent = location.state?.[EXTERNAL_EVENT_STATE_KEY];
    if (isSameExternalEvent(stateEvent, copyExternalEventId)) {
      applyExternalEventPrefill(stateEvent);
      return;
    }

    if (!copyCompetition) {
      setErrors({ global: COPY_PREFILL_RETRY_MESSAGE });
      return;
    }

    setInitialLoading(true);
    searchExternalEvents(copyCompetition)
      .then((items) => {
        const externalEvents = Array.isArray(items) ? items : [];
        const externalEvent = externalEvents.find((item) => isSameExternalEvent(item, copyExternalEventId));
        if (!externalEvent) {
          setErrors({ global: COPY_PREFILL_NOT_FOUND_MESSAGE });
          return;
        }
        applyExternalEventPrefill(externalEvent);
      })
      .catch((error) => setErrors({ global: getCopyPrefillError(error) }))
      .finally(() => setInitialLoading(false));
  }, [applyExternalEventPrefill, copyCompetition, copyExternalEventId, isEditMode, location.state]);

  // ===================================================================================================================
  // = Implementation
  // ===================================================================================================================

  const handleCategoryChange = (value) => {
    setCategory(value);
    setDrawPolicy(getDefaultDrawPolicy(value));
    setEventTypeId('');
    setEventType(null);
    setErrors((prev) => ({ ...prev, customCategory: undefined, outcomes: undefined }));
  };

  const handleEventTypeChange = (value) => {
    setEventTypeId(value);
    if (!value) {
      setEventType(null);
      setCategory(EVENT_CATEGORY_DEFAULT);
      setCustomCategory('');
      setDrawPolicy(getDefaultDrawPolicy(EVENT_CATEGORY_DEFAULT));
      setOutcomes([]);
    }
  };

  const handleDrawPolicyChange = (value) => {
    setDrawPolicy(value);
    setErrors((prev) => ({ ...prev, drawPolicy: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Название обязательно';
    if (!startDate) errs.startDate = 'Дата начала обязательна';
    if (!endDate) errs.endDate = 'Дата окончания обязательна';
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      errs.endDate = 'Дата окончания должна быть позже даты начала';
    }
    if (category === EVENT_CATEGORY_OTHER && !customCategory.trim()) {
      errs.customCategory = 'Укажи свою категорию';
    }
    if (effectiveScoreBased || isSchemeBased) {
      if (effectiveScoreBased && !drawPolicy) {
        errs.drawPolicy = 'Выбери политику ничьей';
      }
    } else if (outcomes.length === 0) {
      errs.outcomes = 'Добавьте хотя бы один исход';
    }
    return errs;
  };

  const handleAddOutcome = () => {
    const trimmed = newOutcome.trim();
    if (!trimmed) return;
    if (outcomes.includes(trimmed)) {
      setErrors((prev) => ({ ...prev, newOutcome: 'Такой исход уже добавлен' }));
      return;
    }
    setOutcomes((prev) => [...prev, trimmed]);
    setNewOutcome('');
    setErrors((prev) => ({ ...prev, newOutcome: undefined, outcomes: undefined }));
  };

  const handleOutcomeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOutcome();
    }
  };

  const handleRemoveOutcome = (outcome) => {
    setOutcomes((prev) => prev.filter((o) => o !== outcome));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrors({});
    try {
      const event = await saveEvent(buildPayload());
      navigate(`/events/${event.id}`);
    } catch (error) {
      setErrors({ global: getSubmitError(error) });
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    let finalDrawPolicy = (effectiveScoreBased && drawPolicy) ? drawPolicy : null;
    let finalOutcomes;

    if (isEventTypeDriven) {
      if (eventType.outcomeScheme === OutcomeScheme.SCORE_BASED) {
        finalDrawPolicy = eventType.scoreDrawPolicy || drawPolicy;
      } else {
        finalDrawPolicy = null;
      }
      finalOutcomes = generateOutcomesFromEventType(eventType);
    } else {
      finalOutcomes = (scoreBased && drawPolicy) ? generateOutcomesFromDrawPolicy(drawPolicy) : outcomes;
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      startDate: toUserOffsetDateTime(startDate),
      endDate: toUserOffsetDateTime(endDate),
      drawPolicy: finalDrawPolicy,
      outcomes: finalOutcomes,
    };
    if (isEventTypeDriven) {
      payload.eventTypeId = Number(eventTypeId);
    } else {
      payload.category = category;
      if (category === EVENT_CATEGORY_OTHER) {
        payload.customCategory = customCategory.trim();
      }
    }
    return payload;
  };

  const saveEvent = (payload) => {
    if (isEditMode) return updateEvent(id, payload);
    if (isCopyMode) return copyExternalEvent(copyExternalEventId, payload);
    return createEvent(payload);
  };

  const getSubmitError = (error) => {
    const status = error?.response?.status;
    if (status === FORBIDDEN_STATUS) {
      return 'Можно редактировать только свои события, созданные вручную до начала.';
    }
    if (status === BAD_REQUEST_STATUS) {
      return error?.response?.data?.message || 'Проверь даты события.';
    }
    return `Не удалось ${getSubmitActionLabel()} событие. Попробуй ещё раз.`;
  };

  const getSubmitActionLabel = () => {
    if (isEditMode) return 'сохранить';
    if (isCopyMode) return 'создать копию';
    return 'создать';
  };

  const getSubmitButtonLabel = () => {
    if (isEditMode) return 'Сохранить';
    if (isCopyMode) return 'Создать свою копию';
    return 'Создать событие';
  };

  if (initialLoading) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 600 }}>
        <SkeletonCard />
      </div>
    );
  }

  if (notEditable) {
    return (
      <EmptyState
        icon={<PiWarningDuotone size={40} />}
        title="Редактирование недоступно"
        description="Можно редактировать только свои события, созданные вручную, пока они не начались."
        action={
          <Button
            variant="ghost"
            icon={<PiArrowLeftDuotone size={16} />}
            onClick={() => navigate(`/events/${id}`)}
          >
            К событию
          </Button>
        }
      />
    );
  }

  return (
    <div className="bm-animate-in" style={{ maxWidth: 600 }}>
      <Button
        variant="ghost"
        size="sm"
        icon={<PiArrowLeftDuotone size={16} />}
        onClick={() => navigate(backPath)}
        style={{ marginBottom: 'var(--bm-space-lg)' }}
      >
        {isEditMode ? 'Назад к событию' : isCopyMode ? 'Назад к матчам API' : 'Назад к событиям'}
      </Button>

      <h1 className="bm-h2" style={{ marginBottom: 'var(--bm-space-xl)' }}>
        {pageTitle}
      </h1>

      {isCopyMode && (
        <Card variant="glass" style={{ marginBottom: 'var(--bm-space-lg)' }}>
          <div className="bm-font-medium">Копия внешнего события</div>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 6 }}>
            Источник: {copySourceTitle || copyExternalEventId}. После сохранения это будет твоё ручное событие:
            без externalId, официального результата и автоматической синхронизации внешнего API.
          </p>
        </Card>
      )}

      <Card>
        {errors.global && (
          <div className="bm-auth-error" style={{ marginBottom: 'var(--bm-space-md)' }}>
            {errors.global}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}
        >
          <Input
            label="Название *"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: Финал турнира"
            error={errors.title}
          />

          <Input
            label="Описание"
            as="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Краткое описание события (необязательно)"
            rows={3}
          />

          {!isEventTypeDriven && (
            <>
              <Select
                label="Категория *"
                value={category}
                onChange={handleCategoryChange}
                options={EVENT_CATEGORY_OPTIONS}
              />

              {category === EVENT_CATEGORY_OTHER && (
                <Input
                  label="Своя категория *"
                  type="text"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Например: Формула-1"
                  error={errors.customCategory}
                />
              )}
            </>
          )}

          <EventTypeSelectForEvent
            eventTypes={eventTypes}
            value={eventTypeId}
            onChange={handleEventTypeChange}
          />
          {isEventTypeDriven && (
            <Card variant="glass" style={{ marginTop: 'calc(var(--bm-space-sm) * -1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {outcomeSchemeIcon(eventType.outcomeScheme)}
                <span className="bm-font-medium">{eventType.name}</span>
              </div>
              <p className="bm-text-secondary bm-text-sm">
                Схема исходов: {outcomeSchemeLabel(eventType.outcomeScheme)}
                {' • '}{formatEventTypeOutcomes(eventType)}
              </p>
            </Card>
          )}

          <DateTimePicker
            label="Дата начала *"
            value={startDate}
            onChange={setStartDate}
            error={errors.startDate}
          />

          <DateTimePicker
            label="Дата окончания *"
            value={endDate}
            onChange={setEndDate}
            error={errors.endDate}
          />

          {effectiveScoreBased ? (
            <ScoreBasedOutcomesBlock
              drawPolicy={drawPolicy}
              onDrawPolicyChange={handleDrawPolicyChange}
              drawPolicyError={errors.drawPolicy}
              eventType={eventType}
            />
          ) : isSchemeBased && !isCustomScheme(outcomeScheme) ? (
            <SchemeBasedOutcomesBlock eventType={eventType} />
          ) : isCustomScheme(outcomeScheme) ? (
            <CustomSchemeOutcomesBlock
              eventType={eventType}
            />
          ) : (
            <LegacyOutcomesBlock
              outcomes={outcomes}
              newOutcome={newOutcome}
              onNewOutcomeChange={setNewOutcome}
              onAdd={handleAddOutcome}
              onRemove={handleRemoveOutcome}
              onKeyDown={handleOutcomeKeyDown}
              errors={errors}
            />
          )}

          <div
            style={{
              display: 'flex',
              gap: 'var(--bm-space-sm)',
              justifyContent: 'flex-end',
              paddingTop: 'var(--bm-space-sm)',
            }}
          >
            <Button variant="ghost" type="button" onClick={() => navigate(backPath)}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" loading={loading}>
              {getSubmitButtonLabel()}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ===================================================================================================================
// = Score-based block: DrawPolicy + предпросмотр исходов + список правил
// ===================================================================================================================

function ScoreBasedOutcomesBlock({ drawPolicy, onDrawPolicyChange, drawPolicyError, eventType }) {
  const drawPolicyOptions = getDrawPolicyOptions();
  const applicableRules = getApplicableScoringRules();
  const allowedOutcomes = drawPolicy ? generateOutcomesFromDrawPolicy(drawPolicy) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
      <Select
        label="Политика ничьей *"
        value={drawPolicy || ''}
        onChange={onDrawPolicyChange}
        options={drawPolicyOptions}
        error={drawPolicyError}
      />

      <div
        style={{
          padding: 'var(--bm-space-md)',
          background: 'var(--bm-bg-elevated)',
          borderRadius: 'var(--bm-radius-sm)',
          border: '1px solid var(--bm-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--bm-space-sm)',
        }}
      >
        <div
          className="bm-font-medium"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <PiTrophyDuotone size={16} />
Допустимые исходы пари
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {allowedOutcomes.map((code) => (
            <span
              key={code}
              style={{
                padding: '4px 10px',
                background: 'var(--bm-bg)',
                border: '1px solid var(--bm-border)',
                borderRadius: 999,
                fontSize: 13,
              }}
              title={outcomeCodeLabel(code)}
            >
              {code}
            </span>
          ))}
        </div>
        <p className="bm-text-secondary bm-text-sm" style={{ margin: 0 }}>
Участники пари смогут указывать счёт и итоговый код исхода ({formatAllowedOutcomes(drawPolicy)}).
          Эти исходы используются для Scoring v2 правил расчёта очков.
        </p>
      </div>

      <div
        style={{
          padding: 'var(--bm-space-md)',
          background: 'var(--bm-bg-elevated)',
          borderRadius: 'var(--bm-radius-sm)',
          border: '1px solid var(--bm-border)',
        }}
      >
        <div
          className="bm-font-medium"
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
        >
          <PiCalendarDuotone size={16} />
          Применимые правила очков
        </div>
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            color: 'var(--bm-text-secondary)',
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {applicableRules.map((rule) => (
            <li key={rule.code}>
              <strong>{rule.code}</strong> — {rule.label}
            </li>
          ))}
        </ul>
        <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8, marginBottom: 0 }}>
Конкретные очки за каждое правило настраиваются в шаблоне комнаты или при создании пари.
        </p>
      </div>
    </div>
  );
}

// ===================================================================================================================
// = Legacy block: ручной ввод произвольных исходов
// ===================================================================================================================

function LegacyOutcomesBlock({ outcomes, newOutcome, onNewOutcomeChange, onAdd, onRemove, onKeyDown, errors }) {
  return (
    <div>
      <label className="bm-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <PiListBulletsDuotone size={16} />
        Исходы события *
      </label>

      <div style={{ display: 'flex', gap: 'var(--bm-space-sm)', marginBottom: 'var(--bm-space-sm)' }}>
        <Input
          type="text"
          value={newOutcome}
          onChange={(e) => onNewOutcomeChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Например: Победа команды А"
          error={errors.newOutcome}
          style={{ flex: 1 }}
        />
        <Button
          variant="secondary"
          size="md"
          onClick={onAdd}
          type="button"
        >
          Добавить
        </Button>
      </div>

      {errors.outcomes && (
        <span className="bm-input-error-msg">{errors.outcomes}</span>
      )}

      {outcomes.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--bm-space-xs)',
            marginTop: 'var(--bm-space-sm)',
          }}
        >
          {outcomes.map((outcome) => (
            <div
              key={outcome}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: 'var(--bm-bg-elevated)',
                borderRadius: 'var(--bm-radius-sm)',
                border: '1px solid var(--bm-border)',
              }}
            >
              <span className="bm-text-secondary" style={{ fontSize: 14 }}>
                {outcome}
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={<PiTrashDuotone size={14} />}
                onClick={() => onRemove(outcome)}
                type="button"
                style={{ color: 'var(--bm-accent-red)', padding: '4px 8px' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventTypeSelectForEvent({ eventTypes, value, onChange }) {
  const options = useMemo(() => {
    const items = Array.isArray(eventTypes) ? eventTypes : [];
    return [
      { value: '', label: 'Без типа (выбрать категорию вручную)' },
      ...items.map((et) => ({
        value: String(et.id),
        label: `${outcomeSchemeIcon(et.outcomeScheme)} ${et.name}`,
      })),
    ];
  }, [eventTypes]);

  return (
    <Select
      label="Тип события"
      value={value}
      onChange={onChange}
      options={options}
    />
  );
}

function SchemeBasedOutcomesBlock({ eventType }) {
  const previewOutcomes = eventType ? generateOutcomesFromEventType(eventType) : [];
  const scheme = eventType?.outcomeScheme;
  return (
    <div
      style={{
        padding: 'var(--bm-space-md)',
        background: 'var(--bm-bg-elevated)',
        borderRadius: 'var(--bm-radius-sm)',
        border: '1px solid var(--bm-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--bm-space-sm)',
      }}
    >
      <div className="bm-font-medium" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {outcomeSchemeIcon(scheme)}
        {outcomeSchemeLabel(scheme)}
      </div>
      <p className="bm-text-secondary bm-text-sm">
        {outcomeSchemeDescription(scheme)}
      </p>
      {previewOutcomes.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {previewOutcomes.map((code) => (
            <span
              key={code}
              style={{
                padding: '4px 10px',
                background: 'var(--bm-bg)',
                border: '1px solid var(--bm-border)',
                borderRadius: 999,
                fontSize: 13,
              }}
            >
              {code}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CustomSchemeOutcomesBlock({ eventType }) {
  const previewOutcomes = eventType ? generateOutcomesFromEventType(eventType) : [];
  return (
    <div
      style={{
        padding: 'var(--bm-space-md)',
        background: 'var(--bm-bg-elevated)',
        borderRadius: 'var(--bm-radius-sm)',
        border: '1px solid var(--bm-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--bm-space-sm)',
      }}
    >
      <div className="bm-font-medium" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <PiTrophyDuotone size={16} />
        Допустимые исходы (определяются типом события)
      </div>
      {previewOutcomes.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {previewOutcomes.map((code) => (
            <span
              key={code}
              style={{
                padding: '4px 10px',
                background: 'var(--bm-bg)',
                border: '1px solid var(--bm-border)',
                borderRadius: 999,
                fontSize: 13,
              }}
            >
              {code}
            </span>
          ))}
        </div>
      ) : (
        <p className="bm-text-secondary bm-text-sm">Произвольные текстовые исходы (legacy)</p>
      )}
    </div>
  );
}
